// src/components/Dashboard/ClaimAttachments.jsx
// Apartado "Attachments" de un reclamo (ClaimDetailPage.jsx): un explorador
// de archivos estilo Finder — una carpeta raíz implícita (el reclamo) que
// contiene carpetas creadas por admin/ajustador, y esas carpetas pueden a
// su vez contener subcarpetas y archivos, sin límite de profundidad. Admin
// y el ajustador dueño del reclamo pueden crear/renombrar/borrar carpetas y
// subir/ver/borrar archivos (solo los propios, salvo admin). Los archivos
// viajan por el mismo backend de respaldos que check/payment
// (backupService.js, tipo 'attachment'), no por Firebase Storage — ver
// attachmentService.js.
import React, { useContext, useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { LanguageContext } from '../../App';
import { useAuth } from '../../contexts/AuthContext';
import { validateBackupFile, ATTACHMENT_ACCEPT } from '../../services/backupService';
import ConfirmModal from '../common/ConfirmModal';
import {
  getAttachmentFolders,
  getAllAttachmentFolders,
  createAttachmentFolder,
  renameAttachmentFolder,
  deleteAttachmentFolder,
  getAttachmentFiles,
  getAllAttachmentFiles,
  uploadAttachmentFile,
  deleteAttachmentFile,
  openAttachmentFile
} from '../../services/attachmentService';

const formatSize = (bytes) => {
  if (!bytes) return '';
  const kb = bytes / 1024;
  return kb < 1024 ? `${kb.toFixed(0)} KB` : `${(kb / 1024).toFixed(1)} MB`;
};

// Plantillas rápidas (Módulo 4 del spec del cliente): un clic crea de una
// las carpetas típicas de un Public Adjuster, en vez de escribirlas a mano
// una por una. Solo aparecen en la raíz del reclamo — son de primer nivel.
const FOLDER_TEMPLATES = [
  { es: 'Recibido del Cliente', en: 'Received from Client' },
  { es: 'Cartas de la Aseguradora', en: 'Insurance Letters' },
  { es: 'Fotos', en: 'Photos' },
  { es: 'Estimados', en: 'Estimates' },
  { es: 'Correspondencia', en: 'Correspondence' }
];

// El backend detecta un .docx por su firma de ZIP (ver backup.php) — el
// mime que termina guardado en Firestore puede ser 'application/zip' en
// vez del "bonito" wordprocessingml, así que el nombre del archivo (su
// extensión) es más confiable acá que el mimeType para distinguir un Word.
const fileIconFor = (mimeType, name) => {
  if (mimeType === 'application/pdf') return 'fa-file-pdf';
  if (mimeType?.startsWith('image/')) return 'fa-file-image';
  if (mimeType?.includes('wordprocessingml') || mimeType === 'application/msword' || /\.docx?$/i.test(name || '')) return 'fa-file-word';
  return 'fa-file';
};

// Tile genérica de carpeta/archivo con acciones (lápiz/basura) en la
// esquina, tal como en el Finder de macOS: aparecen sutiles y se resaltan
// al pasar el mouse, en vez de ocupar espacio permanente en la fila.
const Tile = ({ icon, label, sublabel, onOpen, canEdit, canDelete, onRename, onDelete, renaming, nameDraft, onNameDraftChange, onConfirmRename, onCancelRename, savingName }) => {
  if (renaming) {
    return (
      <div className="cd-attach-tile cd-attach-tile-renaming">
        <div className="cd-attach-tile-icon"><i className={`fa-solid ${icon}`}></i></div>
        <input
          type="text"
          value={nameDraft}
          onChange={(e) => onNameDraftChange(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') onConfirmRename();
            if (e.key === 'Escape') onCancelRename();
          }}
          className="cd-attach-tile-input"
          autoFocus
          disabled={savingName}
        />
        <div className="cd-attach-tile-rename-actions">
          <button type="button" className="cd-attach-mini-btn cd-attach-mini-ok" onClick={onConfirmRename} disabled={savingName} title="Save">
            <i className="fa-solid fa-check"></i>
          </button>
          <button type="button" className="cd-attach-mini-btn" onClick={onCancelRename} disabled={savingName} title="Cancel">
            <i className="fa-solid fa-xmark"></i>
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="cd-attach-tile" onDoubleClick={onOpen}>
      <div className="cd-attach-tile-actions">
        {canEdit && (
          <button type="button" className="cd-attach-mini-btn" onClick={(e) => { e.stopPropagation(); onRename(); }} title="Rename">
            <i className="fa-solid fa-pen"></i>
          </button>
        )}
        {canDelete && (
          <button type="button" className="cd-attach-mini-btn cd-attach-mini-danger" onClick={(e) => { e.stopPropagation(); onDelete(); }} title="Delete">
            <i className="fa-solid fa-trash"></i>
          </button>
        )}
      </div>
      <button type="button" className="cd-attach-tile-body" onClick={onOpen}>
        <div className="cd-attach-tile-icon"><i className={`fa-solid ${icon}`}></i></div>
        <span className="cd-attach-tile-label" title={label}>{label}</span>
        {sublabel && <span className="cd-attach-tile-sublabel" title={typeof sublabel === 'string' ? sublabel : undefined}>{sublabel}</span>}
      </button>
    </div>
  );
};

const ClaimAttachments = ({ claimRecordId }) => {
  const { language } = useContext(LanguageContext);
  const { currentUser, isAdmin } = useAuth();

  // Pila de navegación: [] = raíz del reclamo. Cada entrada es la carpeta
  // abierta en ese nivel — el breadcrumb se dibuja directo desde acá.
  const [path, setPath] = useState([]);
  const currentFolder = path.length ? path[path.length - 1] : null;

  const [subfolders, setSubfolders] = useState([]);
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);

  const [creatingFolder, setCreatingFolder] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [savingNewFolder, setSavingNewFolder] = useState(false);

  const [renamingFolderId, setRenamingFolderId] = useState(null);
  const [renameDraft, setRenameDraft] = useState('');
  const [savingRename, setSavingRename] = useState(false);

  const [search, setSearch] = useState('');
  const [searching, setSearching] = useState(false);
  const [searchResults, setSearchResults] = useState(null);
  const [allFolders, setAllFolders] = useState([]);

  // Confirmación de borrado — pedido 2026-09-22: "no puedo borrar el pdf
  // que subí". Antes usaba window.confirm(), el diálogo nativo del
  // navegador, que viola la política del proyecto (ver ConfirmModal.jsx:
  // "reemplaza window.confirm() en toda la app") y además es frágil — tras
  // varios confirm() seguidos, Chrome ofrece "no volver a preguntar" y de
  // ahí en más confirm() devuelve false en silencio, sin mostrar nada, lo
  // que hace parecer que "borrar" no hace nada.
  const [pendingDeleteFile, setPendingDeleteFile] = useState(null);
  const [deletingFile, setDeletingFile] = useState(false);
  const [pendingDeleteFolder, setPendingDeleteFolder] = useState(null);
  const [deletingFolder, setDeletingFolder] = useState(false);

  const currentEmail = currentUser?.email;

  const loadLevel = async (folderId) => {
    if (!claimRecordId) return;
    setLoading(true);
    try {
      const [nextFolders, nextFiles] = await Promise.all([
        getAttachmentFolders(claimRecordId, folderId),
        folderId ? getAttachmentFiles(folderId) : Promise.resolve([])
      ]);
      setSubfolders(nextFolders);
      setFiles(nextFiles);
    } catch (err) {
      toast.error(language === 'es' ? 'No se pudo cargar el contenido.' : 'Could not load contents.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadLevel(currentFolder?.id || null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [claimRecordId, currentFolder?.id]);

  // Buscador: dispara una búsqueda global (todos los archivos del reclamo,
  // en cualquier carpeta/subcarpeta) medio segundo después de que el
  // usuario deja de teclear, en vez de en cada tecla.
  useEffect(() => {
    const term = search.trim();
    if (!term) {
      setSearchResults(null);
      return;
    }
    let active = true;
    setSearching(true);
    const timeout = setTimeout(async () => {
      try {
        const [allFiles, folders] = await Promise.all([
          getAllAttachmentFiles(claimRecordId),
          getAllAttachmentFolders(claimRecordId)
        ]);
        if (!active) return;
        setAllFolders(folders);
        const lower = term.toLowerCase();
        setSearchResults(allFiles.filter(f => f.name?.toLowerCase().includes(lower)));
      } catch (err) {
        if (active) toast.error(language === 'es' ? 'No se pudo buscar.' : 'Search failed.');
      } finally {
        if (active) setSearching(false);
      }
    }, 400);
    return () => { active = false; clearTimeout(timeout); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, claimRecordId]);

  const folderNameById = useMemo(() => {
    const map = {};
    allFolders.forEach(f => { map[f.id] = f.name; });
    return map;
  }, [allFolders]);

  const handleOpenFolder = (folder) => setPath(current => [...current, folder]);
  const handleBreadcrumbClick = (index) => setPath(current => current.slice(0, index));

  const handleCreateFolder = async () => {
    const name = newFolderName.trim();
    if (!name) { setCreatingFolder(false); return; }
    setSavingNewFolder(true);
    try {
      await createAttachmentFolder(claimRecordId, currentFolder?.id || null, name);
      setCreatingFolder(false);
      setNewFolderName('');
      toast.success(language === 'es' ? 'Carpeta creada.' : 'Folder created.');
      await loadLevel(currentFolder?.id || null);
    } catch (err) {
      toast.error(err.message || (language === 'es' ? 'No se pudo crear la carpeta.' : 'Could not create the folder.'));
    } finally {
      setSavingNewFolder(false);
    }
  };

  const [showTemplates, setShowTemplates] = useState(false);

  const handleCreateFromTemplate = async (name) => {
    setShowTemplates(false);
    try {
      await createAttachmentFolder(claimRecordId, null, name);
      toast.success(language === 'es' ? 'Carpeta creada.' : 'Folder created.');
      await loadLevel(null);
    } catch (err) {
      toast.error(err.message || (language === 'es' ? 'No se pudo crear la carpeta.' : 'Could not create the folder.'));
    }
  };

  const handleStartRename = (folder) => {
    setRenamingFolderId(folder.id);
    setRenameDraft(folder.name);
  };

  const handleConfirmRename = async () => {
    const trimmed = renameDraft.trim();
    if (!trimmed) { setRenamingFolderId(null); return; }
    setSavingRename(true);
    try {
      await renameAttachmentFolder(renamingFolderId, trimmed);
      setSubfolders(current => current.map(f => f.id === renamingFolderId ? { ...f, name: trimmed } : f));
      setPath(current => current.map(f => f.id === renamingFolderId ? { ...f, name: trimmed } : f));
      setRenamingFolderId(null);
      toast.success(language === 'es' ? 'Carpeta renombrada.' : 'Folder renamed.');
    } catch (err) {
      toast.error(language === 'es' ? 'No se pudo renombrar la carpeta.' : 'Could not rename the folder.');
    } finally {
      setSavingRename(false);
    }
  };

  const handleDeleteFolder = async () => {
    if (!pendingDeleteFolder) return;
    setDeletingFolder(true);
    try {
      await deleteAttachmentFolder(pendingDeleteFolder.id);
      setSubfolders(current => current.filter(f => f.id !== pendingDeleteFolder.id));
      toast.success(language === 'es' ? 'Carpeta eliminada.' : 'Folder deleted.');
      setPendingDeleteFolder(null);
    } catch (err) {
      toast.error(language === 'es' ? 'No se pudo eliminar la carpeta.' : 'Could not delete the folder.');
    } finally {
      setDeletingFolder(false);
    }
  };

  const handleUpload = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file || !currentFolder) return;
    try {
      validateBackupFile(file, ATTACHMENT_ACCEPT);
      setUploading(true);
      await uploadAttachmentFile({ file, claimRecordId, folderId: currentFolder.id });
      toast.success(language === 'es' ? 'Archivo subido.' : 'File uploaded.');
      await loadLevel(currentFolder.id);
    } catch (err) {
      toast.error(err.message || (language === 'es' ? 'No se pudo subir el archivo.' : 'Could not upload the file.'));
    } finally {
      setUploading(false);
    }
  };

  const handleOpenFile = async (storageKey) => {
    try {
      await openAttachmentFile(storageKey);
    } catch (err) {
      toast.error(language === 'es' ? 'No se pudo abrir el archivo.' : 'Could not open the file.');
    }
  };

  const handleDeleteFile = async () => {
    if (!pendingDeleteFile) return;
    const { file, removeLocal } = pendingDeleteFile;
    setDeletingFile(true);
    try {
      await deleteAttachmentFile(file.id);
      toast.success(language === 'es' ? 'Archivo eliminado.' : 'File deleted.');
      removeLocal();
      setPendingDeleteFile(null);
    } catch (err) {
      toast.error(language === 'es' ? 'No se pudo eliminar el archivo.' : 'Could not delete the file.');
    } finally {
      setDeletingFile(false);
    }
  };

  if (!claimRecordId) return null;

  const isSearching = search.trim().length > 0;
  const isEmpty = !loading && subfolders.length === 0 && files.length === 0 && !creatingFolder;

  return (
    <div className="cd-attach-section claim-files">
      <div className="cd-detail-checks-header">
        <h2 className="cd-detail-checks-title">{language === 'es' ? 'Archivos adjuntos' : 'Attached files'}</h2>
        <p className="cd-section-hint">{language === 'es' ? 'Archivos que subes tú (fotos, PDF, cartas del cliente o la aseguradora). Los documentos que arma la app están en Documentos generados.' : 'Files you upload (photos, PDFs, letters from the client or insurance company). Documents the app builds are in Generated documents.'}</p>
      </div>

      <div className="cd-attach-explorer">
        <div className="cd-attach-toolbar">
          <nav className="cd-attach-breadcrumb" aria-label={language === 'es' ? 'Ubicación de archivos' : 'File location'}>
            <button type="button" className={`cd-attach-crumb ${!currentFolder ? 'cd-attach-crumb-current' : ''}`} onClick={() => handleBreadcrumbClick(0)} disabled={isSearching}>
              <i className="fa-solid fa-folder-tree"></i> {language === 'es' ? 'Reclamo' : 'Claim'}
            </button>
            {path.map((folder, i) => (
              <React.Fragment key={folder.id}>
                <i className="fa-solid fa-chevron-right cd-attach-crumb-sep"></i>
                <button
                  type="button"
                  className={`cd-attach-crumb ${i === path.length - 1 ? 'cd-attach-crumb-current' : ''}`}
                  onClick={() => handleBreadcrumbClick(i + 1)}
                  disabled={isSearching || i === path.length - 1}
                >
                  {folder.name}
                </button>
              </React.Fragment>
            ))}
          </nav>

          <div className="cd-attach-toolbar-actions">
            <div className="cd-attach-search">
              <i className="fa-solid fa-magnifying-glass"></i>
              <input
                type="text"
                aria-label={language === 'es' ? 'Buscar archivos' : 'Search files'}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={language === 'es' ? 'Buscar archivos...' : 'Search files...'}
              />
              {search && (
                <button type="button" className="cd-attach-search-clear" aria-label={language === 'es' ? 'Limpiar búsqueda' : 'Clear search'} onClick={() => setSearch('')}>
                  <i className="fa-solid fa-xmark"></i>
                </button>
              )}
            </div>
            {!isSearching && (
              <button type="button" className="cd-attach-toolbar-btn" onClick={() => { setCreatingFolder(true); setNewFolderName(''); }} title={language === 'es' ? 'Nueva carpeta' : 'New folder'}>
                <i className="fa-solid fa-folder-plus" aria-hidden="true"></i><span>{language === 'es' ? 'Nueva carpeta' : 'New folder'}</span>
              </button>
            )}
            {!isSearching && !currentFolder && (
              <div className="cd-attach-templates-wrap">
                <button type="button" className="cd-attach-toolbar-btn" aria-expanded={showTemplates} onClick={() => setShowTemplates(v => !v)} title={language === 'es' ? 'Plantillas de carpeta' : 'Folder templates'}>
                  <i className="fa-solid fa-layer-group" aria-hidden="true"></i><span>{language === 'es' ? 'Plantillas' : 'Templates'}</span>
                </button>
                {showTemplates && (
                  <div className="cd-attach-templates-menu">
                    <span className="cd-attach-templates-title">{language === 'es' ? 'Carpetas típicas' : 'Common folders'}</span>
                    {FOLDER_TEMPLATES.map(t => (
                      <button key={t.en} type="button" className="cd-attach-templates-item" onClick={() => handleCreateFromTemplate(language === 'es' ? t.es : t.en)}>
                        <i className="fa-solid fa-folder"></i> {language === 'es' ? t.es : t.en}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
            {!isSearching && currentFolder && (
              <label className={`cd-attach-toolbar-btn ${uploading ? 'cd-attach-disabled' : ''}`} title={language === 'es' ? 'Subir archivo' : 'Upload file'}>
                <i className="fa-solid fa-upload" aria-hidden="true"></i><span>{language === 'es' ? 'Subir archivo' : 'Upload file'}</span>
                <input type="file" accept={ATTACHMENT_ACCEPT} onChange={handleUpload} disabled={uploading} hidden />
              </label>
            )}
          </div>
        </div>

        <div className="cd-attach-body">
          {isSearching ? (
            <>
              {searching && <p className="cd-attach-empty">{language === 'es' ? 'Buscando...' : 'Searching...'}</p>}
              {!searching && (searchResults?.length || 0) === 0 && (
                <p className="cd-attach-empty">{language === 'es' ? 'Sin resultados.' : 'No results.'}</p>
              )}
              {!searching && searchResults?.length > 0 && (
                <div className="cd-attach-grid">
                  {searchResults.map(file => (
                    <Tile
                      key={file.id}
                      icon={fileIconFor(file.mimeType, file.name)}
                      label={file.name}
                      sublabel={folderNameById[file.folderId] || ''}
                      onOpen={() => handleOpenFile(file.storageKey)}
                      canEdit={false}
                      canDelete={isAdmin || file.uploadedByEmail === currentEmail}
                      onDelete={() => setPendingDeleteFile({ file, removeLocal: () => setSearchResults(current => current.filter(f => f.id !== file.id)) })}
                    />
                  ))}
                </div>
              )}
            </>
          ) : (
            <>
              {loading && <p className="cd-attach-empty">{language === 'es' ? 'Cargando...' : 'Loading...'}</p>}
              {isEmpty && (
                <p className="cd-attach-empty">
                  {currentFolder
                    ? (language === 'es' ? 'Esta carpeta está vacía. Usa los botones de arriba para agregar una subcarpeta o subir un archivo.' : 'This folder is empty. Use the buttons above to add a subfolder or upload a file.')
                    : (language === 'es' ? 'Todavía no hay carpetas. Usa el botón de arriba para crear la primera.' : 'No folders yet. Use the button above to create the first one.')}
                </p>
              )}
              {!loading && (subfolders.length > 0 || files.length > 0 || creatingFolder) && (
                <div className="cd-attach-grid">
                  {subfolders.map(folder => (
                    <Tile
                      key={folder.id}
                      icon="fa-folder"
                      label={folder.name}
                      sublabel={folder.createdByName || folder.createdByEmail}
                      onOpen={() => handleOpenFolder(folder)}
                      canEdit={isAdmin || folder.createdByEmail === currentEmail}
                      canDelete={isAdmin || folder.createdByEmail === currentEmail}
                      onRename={() => handleStartRename(folder)}
                      onDelete={() => setPendingDeleteFolder(folder)}
                      renaming={renamingFolderId === folder.id}
                      nameDraft={renameDraft}
                      onNameDraftChange={setRenameDraft}
                      onConfirmRename={handleConfirmRename}
                      onCancelRename={() => setRenamingFolderId(null)}
                      savingName={savingRename}
                    />
                  ))}
                  {creatingFolder && (
                    <Tile
                      icon="fa-folder"
                      label=""
                      renaming
                      nameDraft={newFolderName}
                      onNameDraftChange={setNewFolderName}
                      onConfirmRename={handleCreateFolder}
                      onCancelRename={() => setCreatingFolder(false)}
                      savingName={savingNewFolder}
                    />
                  )}
                  {files.map(file => (
                    <Tile
                      key={file.id}
                      icon={fileIconFor(file.mimeType, file.name)}
                      label={file.name}
                      sublabel={formatSize(file.size)}
                      onOpen={() => handleOpenFile(file.storageKey)}
                      canEdit={false}
                      canDelete={isAdmin || file.uploadedByEmail === currentEmail}
                      onDelete={() => setPendingDeleteFile({ file, removeLocal: () => setFiles(current => current.filter(f => f.id !== file.id)) })}
                    />
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </div>

      <ConfirmModal
        open={!!pendingDeleteFile}
        icon="fa-trash"
        danger
        title={language === 'es' ? 'Eliminar archivo' : 'Delete file'}
        message={pendingDeleteFile ? (language === 'es' ? `¿Eliminar "${pendingDeleteFile.file.name}"? No se puede deshacer.` : `Delete "${pendingDeleteFile.file.name}"? This cannot be undone.`) : ''}
        confirmLabel={deletingFile ? (language === 'es' ? 'Eliminando…' : 'Deleting…') : (language === 'es' ? 'Eliminar' : 'Delete')}
        cancelLabel={language === 'es' ? 'Cancelar' : 'Cancel'}
        loading={deletingFile}
        onConfirm={handleDeleteFile}
        onCancel={() => setPendingDeleteFile(null)}
      />

      <ConfirmModal
        open={!!pendingDeleteFolder}
        icon="fa-trash"
        danger
        title={language === 'es' ? 'Eliminar carpeta' : 'Delete folder'}
        message={pendingDeleteFolder ? (language === 'es' ? `¿Eliminar la carpeta "${pendingDeleteFolder.name}" y todo su contenido? No se puede deshacer.` : `Delete folder "${pendingDeleteFolder.name}" and everything inside it? This cannot be undone.`) : ''}
        confirmLabel={deletingFolder ? (language === 'es' ? 'Eliminando…' : 'Deleting…') : (language === 'es' ? 'Eliminar' : 'Delete')}
        cancelLabel={language === 'es' ? 'Cancelar' : 'Cancel'}
        loading={deletingFolder}
        onConfirm={handleDeleteFolder}
        onCancel={() => setPendingDeleteFolder(null)}
      />
    </div>
  );
};

export default ClaimAttachments;
